import type { ComputedRef, Ref } from '@vue/composition-api';
import { computed, ref } from '@vue/composition-api';

export interface UseStepperReturn<StepName, Steps, Step> {
  steps: Readonly<Ref<Steps>>;
  stepNames: Readonly<Ref<StepName[]>>;
  index: Ref<number>;
  current: ComputedRef<Step>;
  next: ComputedRef<StepName | undefined>;
  previous: ComputedRef<StepName | undefined>;
  isFirst: ComputedRef<boolean>;
  isLast: ComputedRef<boolean>;
  at: (index: number) => Step | undefined;
  get: (step: StepName) => Step | undefined;
  goTo: (step: StepName) => void;
  goToNext: () => void;
  goToPrevious: () => void;
  goBackTo: (step: StepName) => void;
  isNext: (step: StepName) => boolean;
  isPrevious: (step: StepName) => boolean;
  isCurrent: (step: StepName) => boolean;
  isBefore: (step: StepName) => boolean;
  isAfter: (step: StepName) => boolean;
}

export function useStepper<T extends string | number>(steps: T[], initialStep?: T): UseStepperReturn<T, T[], T>;
export function useStepper<T extends Record<string, any>>(
  steps: T,
  initialStep?: keyof T
): UseStepperReturn<Exclude<keyof T, symbol>, T, T[keyof T]>;
export function useStepper(steps: any, initialStep?: any): UseStepperReturn<any, any, any> {
  const stepsRef = ref<any[]>(Array.isArray(steps) ? steps : Object.keys(steps));
  const stepNames = computed<any[]>(() => stepsRef.value);
  const index = ref(stepNames.value.indexOf(initialStep ?? stepNames.value[0]));
  const current = computed(() => at(index.value));
  const isFirst = computed(() => index.value === 0);
  const isLast = computed(() => index.value === stepNames.value.length - 1);
  const next = computed(() => stepNames.value[index.value + 1]);
  const previous = computed(() => stepNames.value[index.value - 1]);

  function at(idx: number) {
    if (Array.isArray(steps)) return steps[idx];
    return steps[stepNames.value[idx]];
  }

  function get(step: any) {
    if (!stepNames.value.includes(step)) return undefined;
    return at(stepNames.value.indexOf(step));
  }

  function goTo(step: any) {
    if (stepNames.value.includes(step)) index.value = stepNames.value.indexOf(step);
  }

  function goToNext() {
    if (!isLast.value) index.value++;
  }

  function goToPrevious() {
    if (!isFirst.value) index.value--;
  }

  function goBackTo(step: any) {
    if (isAfter(step)) goTo(step);
  }

  function isNext(step: any) {
    return stepNames.value.indexOf(step) === index.value + 1;
  }

  function isPrevious(step: any) {
    return stepNames.value.indexOf(step) === index.value - 1;
  }

  function isCurrent(step: any) {
    return stepNames.value.indexOf(step) === index.value;
  }

  function isBefore(step: any) {
    return index.value < stepNames.value.indexOf(step);
  }

  function isAfter(step: any) {
    return index.value > stepNames.value.indexOf(step);
  }

  return {
    steps: stepsRef,
    stepNames,
    index,
    current,
    next,
    previous,
    isFirst,
    isLast,
    at,
    get,
    goTo,
    goToNext,
    goToPrevious,
    goBackTo,
    isNext,
    isPrevious,
    isCurrent,
    isBefore,
    isAfter,
  };
}
